/**
 * Hook to fetch condition details from audit logs.
 *
 * When we have a condition_id reference, we can fetch the condition's
 * audit log to get its full details from when it was created/updated.
 */

import { useServicesQuery } from '@/services/api';
import type { ServicesApiAuditLog } from '@/services/api/services-api';
import { CONDITION_CACHE_STALE_TIME } from '../components/segment-conditions-preview/constants';

interface UseConditionDetailsOptions {
  conditionId: number;
  workspaceId: number;
  enabled?: boolean;
}

interface ConditionDetails {
  id: number;
  name?: string;
  conditions?: Record<string, unknown>;
  data?: Record<string, unknown>;
}

// Wide date range to capture conditions regardless of when they were created.
const CONDITION_DATE_RANGE = (() => {
  const now = Date.now();
  const tenYearsMs = 10 * 365 * 24 * 60 * 60 * 1000;
  const fiveYearsMs = 5 * 365 * 24 * 60 * 60 * 1000;
  return {
    start_time: new Date(now - tenYearsMs).toISOString(),
    end_time: new Date(now + fiveYearsMs).toISOString(),
  };
})();

/**
 * Fetches condition details by looking up the condition's audit log.
 * This retrieves the condition as it was stored at a point in time.
 */
export function useConditionDetails({ conditionId, workspaceId, enabled = true }: UseConditionDetailsOptions): {
  conditionDetails: ConditionDetails | null;
  isLoading: boolean;
  error: string | null;
  rawAuditLog: ServicesApiAuditLog | null;
} {
  const query = useServicesQuery(
    'getWorkspaceAuditLogs',
    {
      params: { environment_id: workspaceId },
      query: {
        object_type: 'Condition',
        object_id: String(conditionId),
        limit: 1,
        start_time: CONDITION_DATE_RANGE.start_time,
        end_time: CONDITION_DATE_RANGE.end_time,
      },
    },
    { enabled: enabled && conditionId > 0 && workspaceId > 0, staleTime: CONDITION_CACHE_STALE_TIME },
  );

  const auditLogs = query.data?.data ?? [];

  // Extract condition details from the audit log data
  let conditionDetails: ConditionDetails | null = null;

  if (auditLogs.length > 0) {
    const auditLog = auditLogs[0];
    const data = auditLog.data as Record<string, unknown> | undefined;

    if (data != null) {
      let extractedConditions: Record<string, unknown> | undefined;
      if (data.conditions != null) {
        extractedConditions = data.conditions as Record<string, unknown>;
      } else if (data.and != null) {
        extractedConditions = { and: data.and };
      } else if (data.or != null) {
        extractedConditions = { or: data.or };
      }

      conditionDetails = {
        id: conditionId,
        name: (data.name as string) ?? undefined,
        conditions: extractedConditions,
        data: data,
      };
    }
  }

  const errorMessage = query.error?.message ?? null;

  return {
    conditionDetails,
    isLoading: query.isLoading,
    error: errorMessage,
    rawAuditLog: auditLogs[0] ?? null,
  };
}

export default useConditionDetails;
